import { action, computed, observable, runInAction } from 'mobx';
import { creditCardsApi } from 'AppClient/api/CreditCards';
import { CreditCard, DnDItem } from 'app-types';
import { FetchStatus } from 'servicesCommon/apiTypes';
import { RootStore } from 'AppClient/stores';
import { ReactStripeElements } from 'react-stripe-elements';
import { getCCDndItems } from 'utils/utils';

export class CCStore {
	@observable private _dndItems: DnDItem[] = [];
	@observable private _activeCreditCards: CreditCard[] = [];
	@observable private _deletedCreditCards: CreditCard[] = [];
	@observable private _expiredCreditCards: CreditCard[] = [];
	@observable private _fetchStatus: FetchStatus = FetchStatus.IDLE;
	@observable public _isCreditCardsModalOpen = false;

	constructor(private rootStore: RootStore) {}

	@computed
	get isCreditCardsModalOpen() {
		return this._isCreditCardsModalOpen;
	}

	@action.bound
	toggleModal() {
		this._isCreditCardsModalOpen = !this._isCreditCardsModalOpen;
	}
	@action.bound
	setCreditCards(creditCards: CreditCard[]) {
		const deletedCreditCards = creditCards.filter(item => item.isDeleted);
		const expiredCreditCards = creditCards.filter(item => item.isExpired);
		const activeCreditCards = creditCards.filter(item => !item.isDeleted && !item.isExpired);

		this._activeCreditCards = [...activeCreditCards];
		this._deletedCreditCards = [...deletedCreditCards];
		this._expiredCreditCards = [...expiredCreditCards];
		this._dndItems = getCCDndItems(this._activeCreditCards);
	}

	@computed
	get dndItems(): DnDItem[] {
		return this._dndItems;
	}

	@computed
	get activeCreditCards(): CreditCard[] {
		return this._activeCreditCards;
	}

	@computed
	get expiredCreditCards(): CreditCard[] {
		return this._expiredCreditCards;
	}

	@computed
	get deletedCreditCards(): CreditCard[] {
		return this._deletedCreditCards;
	}

	@computed
	get isIdle(): boolean {
		return this._fetchStatus === FetchStatus.IDLE;
	}

	@computed
	get isPending(): boolean {
		return this._fetchStatus === FetchStatus.PENDING;
	}

	@computed
	get isSuccess(): boolean {
		return this._fetchStatus === FetchStatus.SUCCESS;
	}

	@computed
	get fetchStatus(): FetchStatus {
		return this._fetchStatus;
	}

	@computed
	get paymentMethodIds(): string[] {
		// credit cards to be saved
		const activeIds = this._activeCreditCards.map(item => item.paymentMethodId);
		const expiredIds = this._expiredCreditCards.map(item => item.paymentMethodId);
		return [...activeIds, ...expiredIds];
	}

	@action.bound
	updateActiveCreditCards(
		actualItems: DnDItem[],
		removedRowNumber?: number,
		removedExpiredCard?: CreditCard
	) {
		if (removedRowNumber !== undefined) {
			this._deletedCreditCards.push(this._activeCreditCards[removedRowNumber]);
		}
		if (removedExpiredCard) {
			this._expiredCreditCards = this._expiredCreditCards.filter(
				item => item.paymentMethodId !== removedExpiredCard.paymentMethodId
			);
			this._deletedCreditCards.push(removedExpiredCard);
		}

		this._activeCreditCards = actualItems.map(item => {
			return this._activeCreditCards[item.rowInitialNumber];
		});
		this._dndItems = getCCDndItems(this._activeCreditCards);
	}

	@action.bound
	async getClientSecret() {
		try {
			const response = await creditCardsApi.createStripeSession();
			const {
				data: { result, error },
			} = response;
			if (error) {
				this.rootStore.alertStore.setAlert(error, 'error');
				this._fetchStatus = FetchStatus.FAILURE;
				return;
			}
			return result;
		} catch (_) {
			this._fetchStatus = FetchStatus.FAILURE;
			return;
		}
	}

	@action.bound
	async getPaymentMethodIdFromStripe(
		stripe: ReactStripeElements.StripeProps,
		cardElement: stripe.elements.Element
	) {
		const clientSecret = await this.getClientSecret();

		if (clientSecret) {
			const stripeResponse = await stripe.confirmCardSetup(
				clientSecret,
				//'seti_1G9APBDkm6q4OlNepHjvWLuq_secret_GgXU3jcFuk40PTt4YRJjod0geQFQXbW',
				{
					payment_method: {
						card: cardElement,
					},
				}
			);

			const { setupIntent } = stripeResponse;
			const stripeError = stripeResponse.error;
			if (stripeError) {
				this.rootStore.alertStore.setAlert(stripeError.message || '', 'error');
				this._fetchStatus = FetchStatus.FAILURE;
				return;
			}

			return (setupIntent && setupIntent.payment_method) || '';
		}
		return;
	}

	@action.bound
	async getCreditCardInfo(paymentMethodId: string) {
		const response = await creditCardsApi.getCreditCardInfo({
			paymentMethodId,
		});

		const {
			data: { result, error },
		} = response;
		if (error) {
			this.rootStore.alertStore.setAlert(error, 'error');
			this._fetchStatus = FetchStatus.FAILURE;
			return;
		}

		return result;
	}

	@action.bound
	async saveCreditCard(
		stripe: ReactStripeElements.StripeProps,
		cardElement: stripe.elements.Element
	) {
		this._fetchStatus = FetchStatus.PENDING;
		try {
			const paymentMethodId = await this.getPaymentMethodIdFromStripe(stripe, cardElement);

			if (paymentMethodId) {
				const addedCardInfo = await this.getCreditCardInfo(paymentMethodId);

				if (addedCardInfo) {
					runInAction(() => {
						this._activeCreditCards.push({
							...addedCardInfo,
							paymentMethodId,
						});
						this._dndItems = getCCDndItems(this._activeCreditCards);
						this._fetchStatus = FetchStatus.SUCCESS;
					});
				}
			}
		} catch (_error) {
			this._fetchStatus = FetchStatus.FAILURE;
		}
	}
}
